import { useState } from 'react';
import { Box, Collapse, Tooltip, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import { tokens } from '../../global/theme/tokens';
import { AiMarkdown } from './AiMarkdown';
import { AiReferenceChip } from './AiReferenceChip';
import { parseFileAttachment, parseReferenceAttachment } from './aiChatEntities';
import { PROPOSE_POSITIONS, positionField } from './positionScanProposal';
import { numberToString } from '../special/numberConverter';
import { entryKind } from '../exo/project-file-explorer/explorerUtils';

const TOOL_RESULT_PREVIEW = 1500;

// the chat-only tool that asks the user questions (backend AskUser)
const ASK_USER = 'ask_user';

export const AiChatMessage = ({ message }) =>
  message.role === 'user' ? (
    message.retry ? (
      <RetryNote />
    ) : (
      <UserMessage message={message} />
    )
  ) : (
    <AssistantMessage message={message} />
  );

// the prompt of a retried turn: the backend continued the interrupted
// answer, the user wrote nothing
const RetryNote = () => {
  const { t } = useTranslation();

  return (
    <Typography
      variant="caption"
      className="self-center"
      sx={{ opacity: 0.6 }}
    >
      {t('The answer was retried.')}
    </Typography>
  );
};

const UserMessage = ({ message }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  // optimistic messages carry what the composer knew, stored ones the API shape
  const references =
    message.localReferences ||
    (message.attachments || []).map(parseReferenceAttachment).filter(Boolean);
  const files =
    message.localAttachments ||
    (message.attachments || []).map(parseFileAttachment).filter(Boolean);

  return (
    <Box className="flex flex-col items-end gap-1 pl-8">
      {(references.length > 0 || files.length > 0) && (
        <Box className="flex flex-wrap justify-end gap-1 max-w-full">
          {references.map((ref) => (
            <AiReferenceChip
              key={`${ref.type}-${ref.id}`}
              reference={ref}
              variant="link"
            />
          ))}
          {files.map((file, index) => (
            <FileChip key={index} file={file} />
          ))}
        </Box>
      )}
      <Box
        className="px-3 py-2 max-w-full"
        sx={{
          backgroundColor: `${colors.info}25`,
          border: `1px solid ${colors.info}40`,
          borderRadius: '16px 16px 4px 16px',
          opacity: message.local ? 0.85 : 1,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: colors.text,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {message.content}
        </Typography>
      </Box>
    </Box>
  );
};

const FileChip = ({ file }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const isProjectFile = file.source === 'project';
  const kind = isProjectFile
    ? entryKind({ name: file.name || '', mimeType: file.mimeType })
    : {
        Icon: file.isImage ? ImageIcon : InsertDriveFileIcon,
        color: colors.text,
      };

  return (
    <Tooltip title={isProjectFile ? file.path : ''} disableInteractive>
      <Box
        className="flex items-center gap-1 px-1.5 py-0.5 rounded-lg min-w-0"
        sx={{ backgroundColor: colors.card, border: `1px solid ${colors.bgInput}` }}
      >
        {isProjectFile && <FolderOpenIcon sx={{ fontSize: 13, opacity: 0.5 }} />}
        <kind.Icon sx={{ fontSize: 14, color: kind.color, opacity: 0.7 }} />
        <Typography
          variant="caption"
          noWrap
          sx={{ color: colors.text, maxWidth: 160 }}
        >
          {file.name}
        </Typography>
      </Box>
    </Tooltip>
  );
};

const AssistantMessage = ({ message }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  // Questions the AI asked are shown as they were answered, not as a
  // step; a call still waiting is shown by the message list's card. A
  // call whose questions the backend rejected (e.g. sent as a string)
  // never reached the user and stays an ordinary failed step.
  const toolCalls = message.toolCalls || [];
  const toolResults = message.toolResults || [];
  const questionCalls = toolCalls.filter(
    (call) =>
      call.name === ASK_USER &&
      Array.isArray(call.arguments?.questions) &&
      toolResults.some((r) => r.id === call.id),
  );
  const isQuestionCall = (id) => questionCalls.some((call) => call.id === id);
  // positions handed to a document form (position scan chats): a list
  // instead of the raw JSON of the step; the dialog offers to add them
  const proposalCalls = toolCalls.filter(
    (call) =>
      call.name === PROPOSE_POSITIONS &&
      Array.isArray(call.arguments?.positions) &&
      toolResults.some((r) => r.id === call.id && !r.failed && !r.denied),
  );
  const isProposalCall = (id) => proposalCalls.some((call) => call.id === id);
  const hidden = (id) => isQuestionCall(id) || isProposalCall(id);

  return (
    <Box className="flex flex-col gap-1 pr-2">
      {message.content && <AiMarkdown content={message.content} />}
      {message.streaming && <StreamingCursor />}
      {message.interrupted && (
        <Box className="flex items-center gap-1" sx={{ color: colors.error }}>
          <ErrorOutlineIcon sx={{ fontSize: 16 }} />
          <Typography variant="caption" fontWeight={500}>
            {t('Answer interrupted')}
          </Typography>
        </Box>
      )}
      {questionCalls.map((call) => (
        <AnsweredQuestions
          key={call.id}
          call={call}
          result={toolResults.find((r) => r.id === call.id)}
        />
      ))}
      {proposalCalls.map((call) => (
        <ProposedPositions
          key={call.id}
          positions={call.arguments.positions}
          result={toolResults.find((r) => r.id === call.id)}
        />
      ))}
      <ToolSteps
        toolCalls={toolCalls.filter((call) => !hidden(call.id))}
        toolResults={toolResults.filter((r) => !hidden(r.id))}
      />
    </Box>
  );
};

/**
 * The positions of a propose_positions call, as the model handed them to
 * the document form (snake_case arguments). A result starting with
 * "Error:" means the form did not take them (structure or picture problem).
 */
const ProposedPositions = ({ positions, result }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const rejected =
    typeof result?.result === 'string' && result.result.startsWith('Error:');
  const lines = positions.filter(
    (position) => !positionField(position, 'is_group'),
  );

  return (
    <Box
      className="flex flex-col gap-1 px-2.5 py-1.5 rounded-lg"
      sx={{ backgroundColor: colors.card, border: `1px solid ${colors.bgInput}` }}
    >
      <Box
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 cursor-pointer select-none"
        sx={{ opacity: 0.8, '&:hover': { opacity: 1 } }}
      >
        <PlaylistAddCheckIcon
          sx={{ fontSize: 16, color: rejected ? colors.warning : colors.info }}
        />
        <Typography variant="caption" fontWeight={600} className="flex-1">
          {rejected ? t('Positions not accepted') : t('Positions proposed')} (
          {lines.length})
        </Typography>
        <ExpandMoreIcon
          sx={{
            fontSize: 16,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: '0.2s ease',
          }}
        />
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box className="flex flex-col gap-0.5">
          {positions.map((position, index) => {
            const field = (key) => positionField(position, key);
            const isGroup = Boolean(field('is_group'));
            return (
              <Box
                key={index}
                className="flex items-baseline gap-2"
                sx={{ pl: `${Math.max(0, parseInt(field('depth'), 10) || 0) * 12}px` }}
              >
                <Typography
                  variant="caption"
                  className="flex-1 min-w-0 truncate"
                  fontWeight={isGroup ? 600 : 400}
                >
                  {field('name')}
                </Typography>
                {!isGroup && (
                  <Typography
                    variant="caption"
                    className="monospace whitespace-nowrap"
                    sx={{ opacity: 0.7 }}
                  >
                    {field('type') === 'percent'
                      ? `${numberToString(Number(field('amount')) || 0, 2)} %`
                      : `${numberToString(Number(field('amount') ?? 1) || 0, 2)} ${field('unit') || ''} × ${numberToString(Number(field('price')) || 0, 2)} €`}
                  </Typography>
                )}
              </Box>
            );
          })}
          {rejected && (
            <Typography variant="caption" sx={{ color: colors.warning, mt: 0.5 }}>
              {result.result}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

/**
 * The questions of an answered ask_user call with what the user replied
 * (the answers are merged into the call's arguments when the turn resumes).
 */
const AnsweredQuestions = ({ call, result }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const questions = call.arguments?.questions || [];
  const answers = call.arguments?.answers || [];
  const skipped = !!result?.denied;

  return (
    <Box
      className="flex flex-col gap-1 px-2.5 py-1.5 rounded-lg"
      sx={{ backgroundColor: colors.card, border: `1px solid ${colors.bgInput}` }}
    >
      <Box
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 cursor-pointer select-none"
        sx={{ opacity: 0.8, '&:hover': { opacity: 1 } }}
      >
        <HelpOutlineIcon sx={{ fontSize: 16, color: colors.info }} />
        <Typography variant="caption" fontWeight={600} className="flex-1">
          {skipped ? t('Questions skipped') : t('Your answers')} ({questions.length})
        </Typography>
        <ExpandMoreIcon
          sx={{
            fontSize: 16,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: '0.2s ease',
          }}
        />
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box className="flex flex-col gap-1">
          {questions.map((question, index) => (
            <Box key={question.id || index} className="flex flex-col">
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                {question.question}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {skipped
                  ? '–'
                  : answerText(
                      answers.find((a) => a.id === question.id),
                      t,
                    )}
              </Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

// an answer as the user gave it: option(s) or yes/no, plus free text
export function answerText(answer, t) {
  if (!answer) return t('Not answered');
  const parts = [];
  if (Array.isArray(answer.value)) {
    if (answer.value.length > 0) parts.push(answer.value.join(', '));
  } else if (typeof answer.value === 'boolean') {
    parts.push(t(answer.value ? 'Yes' : 'No'));
  } else if (answer.value !== null && answer.value !== undefined) {
    parts.push(String(answer.value));
  }
  if (answer.text) parts.push(answer.text);
  return parts.length > 0 ? parts.join(' · ') : t('Not answered');
}

// marks the answer still being generated
const StreamingCursor = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  return (
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: colors.info,
        animation: 'ai-streaming-cursor 1s infinite ease-in-out',
        '@keyframes ai-streaming-cursor': {
          '0%, 100%': { opacity: 0.25 },
          '50%': { opacity: 1 },
        },
      }}
    />
  );
};

const ToolSteps = ({ toolCalls, toolResults }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const steps = [
    ...toolCalls.map((call) => ({
      key: call.id,
      name: call.name,
      args: call.arguments,
      result: toolResults.find((r) => r.id && r.id === call.id),
    })),
    ...toolResults
      .filter((r) => !toolCalls.some((call) => call.id && call.id === r.id))
      .map((result) => ({ key: result.id, name: result.name, result })),
  ];
  if (steps.length === 0) return null;

  const hasWarning = steps.some((s) => s.result?.failed || s.result?.denied);

  return (
    <Box>
      <Box
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-0.5 cursor-pointer select-none rounded-md pr-1.5"
        sx={{
          color: colors.text,
          opacity: 0.6,
          '&:hover': { opacity: 1 },
        }}
      >
        <ExpandMoreIcon
          sx={{
            fontSize: 18,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: '0.2s ease',
          }}
        />
        <Typography variant="caption">
          {open ? t('Hide steps') : t('Show steps')} ({steps.length})
        </Typography>
        {hasWarning && (
          <WarningAmberIcon sx={{ fontSize: 14, color: colors.warning, ml: 0.5 }} />
        )}
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box className="flex flex-col gap-1.5 mt-1">
          {steps.map((step, index) => (
            <ToolStep key={step.key || index} step={step} />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

const ToolStep = ({ step }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const { result } = step;

  const resultText = result?.result || '';
  const args =
    step.args && Object.keys(step.args).length > 0
      ? JSON.stringify(step.args, null, 2)
      : null;

  const codeSx = {
    m: 0,
    mt: 0.5,
    p: 1,
    maxHeight: 160,
    overflow: 'auto',
    fontSize: '0.7rem',
    fontFamily: 'Source Code Pro, monospace',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    backgroundColor: colors.bgInput,
    borderRadius: '6px',
  };

  return (
    <Box
      className="rounded-lg p-2"
      sx={{ backgroundColor: colors.card, border: `1px solid ${colors.bgInput}` }}
    >
      <Box className="flex items-center gap-2">
        <Typography
          variant="caption"
          fontWeight={600}
          sx={{ fontFamily: 'Source Code Pro, monospace' }}
        >
          {step.name || t('Tool')}
        </Typography>
        {result?.failed && (
          <Typography variant="caption" sx={{ color: colors.error }}>
            {t('Failed')}
          </Typography>
        )}
        {result?.denied && (
          <Typography variant="caption" sx={{ color: colors.warning }}>
            {t('Denied')}
          </Typography>
        )}
      </Box>
      {args && (
        <Box component="pre" sx={codeSx}>
          {args}
        </Box>
      )}
      {resultText && (
        <Box component="pre" sx={{ ...codeSx, opacity: 0.8 }}>
          {resultText.length > TOOL_RESULT_PREVIEW
            ? `${resultText.slice(0, TOOL_RESULT_PREVIEW)}…`
            : resultText}
        </Box>
      )}
    </Box>
  );
};
